import { skipToken } from "@reduxjs/toolkit/query";
import {
  getPlaybookNode,
  isPlaybookCriterionNode,
} from "@repo/app-types/entities/playbook";
import {
  getGradingDisplayNames,
  type GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { findMatchingCriteriaFeedback } from "@repo/app-types/helpers/recordingAssessmentFeedbackHelpers";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/index";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { FullAbsoluteTimestamp } from "@repo/ui/timestamps/fullAbsoluteTimestamp";
import { IconShare2 } from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { usePlaybookGradingQuery } from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useMyRecordingAssessmentFeedbackQuery } from "../../api/entities/recording.js";
import { appPaths } from "../../app/appPaths.js";
import { GradingContextInfoTrigger } from "../../components/assessment/gradingContextInfoTrigger.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { GradingValidationDialog } from "./gradingValidationDialog.js";
import { ShareGradingModal } from "./shareGradingModal.js";

interface GradingValidationDialogContainerProps {
  gradingId: GradingId;
  sectionOrCriteriaId: string;
  onCriteriaSelect: (criteriaId: string) => void;
}

export const GradingValidationDialogContainer: FC<
  GradingValidationDialogContainerProps
> = ({ gradingId, sectionOrCriteriaId, onCriteriaSelect }) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId });

  const assessment = grading?.assessment;
  const firstRecording = grading?.recordings.at(0);
  const playbookId = grading?.playbook_id;

  const { data: playbook, isLoading: isPlaybookLoading } = usePlaybookByIdQuery(
    playbookId ?? skipToken,
  );

  const playbookNode = useMemo(() => {
    if (!playbook) {
      return undefined;
    }

    return getPlaybookNode(sectionOrCriteriaId, playbook.assessment_tree);
  }, [playbook, sectionOrCriteriaId]);

  const { data: feedback } = useMyRecordingAssessmentFeedbackQuery(
    firstRecording?.id && playbook?.id
      ? {
          recordingId: firstRecording.id,
          playbookId: playbook.id,
        }
      : skipToken,
  );

  const playbookGrade =
    playbookNode && isPlaybookCriterionNode(playbookNode)
      ? assessment?.criteria_grading[sectionOrCriteriaId]
      : assessment?.section_grading?.[sectionOrCriteriaId];

  const node = playbookNode;
  const gradingContext = node
    ? isPlaybookCriterionNode(node)
      ? node.grading_context || node.description
      : node.description
    : undefined;
  const isLoading = isGradingLoading || isPlaybookLoading;

  const criterionFeedback =
    playbookNode && isPlaybookCriterionNode(playbookNode)
      ? findMatchingCriteriaFeedback(feedback, playbookNode)
      : undefined;

  const displayNames = grading && getGradingDisplayNames(grading);

  return (
    <SlideoverDialog label={node?.name || "Grade"}>
      <SlideoverDialogHeader
        isLoading={isLoading}
        right={
          node && isPlaybookCriterionNode(node) ? (
            <Button
              variant={ButtonVariant.Secondary}
              onClick={() => setIsShareModalOpen(true)}
              size={Size.MEDIUM}
              icon={
                <IconShare2
                  className="text-white-900 h-4 w-4"
                  aria-hidden="true"
                />
              }
            >
              Share
            </Button>
          ) : null
        }
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="group flex min-w-0 items-center gap-1">
            <Heading
              slot="title"
              className="min-w-0 truncate text-base leading-6 font-semibold text-foreground"
            >
              {node?.name}
            </Heading>
            {node && gradingContext && (
              <GradingContextInfoTrigger
                title={node.name}
                content={gradingContext}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {displayNames && (
              <div className="text-sm font-normal text-muted-foreground">
                <Link
                  href={appPaths.playbookCallAssessment.build({
                    playbookId: grading.playbook_id,
                    gradingId: grading.id,
                  })}
                  className={twMerge(
                    "text-muted-foreground no-underline hover:text-gray-700",
                    PH_MASK_CLASS,
                  )}
                >
                  {displayNames.primaryDisplayName}
                </Link>{" "}
                &#x2022;{" "}
                <FullAbsoluteTimestamp timestamp={grading.last_recorded_at} />
              </div>
            )}
          </div>
        </div>
      </SlideoverDialogHeader>
      <SlideoverDialogContentGray>
        {grading && node && assessment && (
          <GradingValidationDialog
            textThread={grading.text_thread ?? undefined}
            gradingId={grading.id}
            playbookId={grading.playbook_id}
            recordings={grading.recordings}
            node={node}
            grade={playbookGrade}
            feedback={criterionFeedback}
            assessment={assessment}
            onCriteriaSelect={onCriteriaSelect}
          />
        )}
      </SlideoverDialogContentGray>

      {grading && node && isPlaybookCriterionNode(node) && (
        <ShareGradingModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          grading={grading}
          criterion={node}
          grade={assessment?.criteria_grading[node.id]}
        />
      )}
    </SlideoverDialog>
  );
};
